import { authService } from './authService';

export enum RoleType {
  ProduktRead = 1,
  ProduktWrite = 2,
  StandortRead = 3,
  StandortWrite = 4,
  PartnerRead = 5,
  PartnerWrite = 6,
  AbholungRead = 7,
  AbholungWrite = 8,
  EinlieferungRead = 9,
  EinlieferungWrite = 10,
  AblieferungRead = 11,
  AblieferungWrite = 12,
  BestandRead = 13,
  BestandWrite = 14,
  Nachweis = 15,
  Nachweisverfolgung = 16,
  Cockpit = 17,
  Report = 18,
  ImportInbound = 19,
  ImportOutbound = 20,
  ZertifikatRead = 21,
  ZertifikatWrite = 22,
  BilanzRead = 23,
  BilanzWrite = 24,
  PocRead = 25,
  GenericImport = 26,
  YtdReport = 27
}

class PermissionService {
  private isInitialized = false;
  private isAdministrator = false;
  private userRoleTypes = new Set<RoleType>();
  private userStandorte: Array<{ nr: string; name: string }> = [];

  async initAsync(): Promise<void> {
    this.userStandorte = [];
    this.userRoleTypes.clear();

    try {
      this.isAdministrator = authService.isAdmin();

      authService.getRollen().forEach((x) => this.userRoleTypes.add(x.typ as RoleType));
      authService
        .getStandorte()
        .forEach((x) => this.userStandorte.push({ nr: x.nr, name: x.name || '' }));

      this.isInitialized = true;
    } catch (error) {
      // ignore...
      this.isInitialized = true;
    }
  }

  getStandorte(): Array<{ nr: string; name: string }> {
    return this.userStandorte;
  }

  hasRole(role: RoleType): boolean {
    if (this.isAdministrator) {
      return true;
    }
    return this.userRoleTypes.has(role);
  }

  hasAnyRole(roles: RoleType[]): boolean {
    if (this.isAdministrator) {
      return true;
    }

    return roles.some((x) => this.userRoleTypes.has(x));
  }

  hasAllRoles(roles: RoleType[]): boolean {
    if (this.isAdministrator) {
      return true;
    }

    return roles.every((x) => this.userRoleTypes.has(x));
  }

  initialized(): boolean {
    return this.isInitialized;
  }

  clear(): void {
    this.isInitialized = false;
    this.isAdministrator = false;
    this.userRoleTypes.clear();
  }

  canViewProdukte(): boolean {
    return this.hasAnyRole([RoleType.ProduktRead, RoleType.ProduktWrite]);
  }

  canEditProdukte(): boolean {
    return this.hasRole(RoleType.ProduktWrite);
  }

  canViewStandorte(): boolean {
    return this.hasAnyRole([RoleType.StandortRead, RoleType.StandortWrite]);
  }

  canEditStandorte(): boolean {
    return this.hasRole(RoleType.StandortWrite);
  }

  canViewPartner(): boolean {
    return this.hasAnyRole([RoleType.PartnerRead, RoleType.PartnerWrite]);
  }

  canEditPartner(): boolean {
    return this.hasRole(RoleType.PartnerWrite);
  }

  canViewAbholungen(): boolean {
    return this.hasAnyRole([RoleType.AbholungRead, RoleType.AbholungWrite]);
  }

  canEditAbholungen(): boolean {
    return this.hasRole(RoleType.AbholungWrite);
  }

  canViewEinlieferungen(): boolean {
    return this.hasAnyRole([RoleType.EinlieferungRead, RoleType.EinlieferungWrite]);
  }

  canEditEinlieferungen(): boolean {
    return this.hasRole(RoleType.EinlieferungWrite);
  }

  canViewAblieferungen(): boolean {
    return this.hasAnyRole([RoleType.AblieferungRead, RoleType.AblieferungWrite]);
  }

  canEditAblieferungen(): boolean {
    return this.hasRole(RoleType.AblieferungWrite);
  }

  canViewBestaende(): boolean {
    return this.hasAnyRole([RoleType.BestandRead, RoleType.BestandWrite]);
  }

  canEditBestaende(): boolean {
    return this.hasRole(RoleType.BestandWrite);
  }

  canViewNachweise(): boolean {
    return this.hasRole(RoleType.Nachweis);
  }

  canViewNachweisverfolgung(): boolean {
    return this.hasRole(RoleType.Nachweisverfolgung);
  }

  canAccessCockpit(): boolean {
    return this.hasRole(RoleType.Cockpit);
  }

  canAccessReports(): boolean {
    return this.hasRole(RoleType.Report);
  }

  canAccessYtdReport(): boolean {
    return this.hasRole(RoleType.YtdReport);
  }

  canAccessGenericImport(): boolean {
    return this.hasRole(RoleType.GenericImport);
  }

  canImportInbound(): boolean {
    return this.hasRole(RoleType.ImportInbound);
  }

  canImportOutbound(): boolean {
    return this.hasRole(RoleType.ImportOutbound);
  }

  canViewZertifikate(): boolean {
    return this.hasRole(RoleType.ZertifikatRead);
  }

  canEditZertifikate(): boolean {
    return this.hasRole(RoleType.ZertifikatWrite);
  }

  canViewBilanzen(): boolean {
    return this.hasRole(RoleType.BilanzRead);
  }

  canEditBilanzen(): boolean {
    return this.hasRole(RoleType.BilanzWrite);
  }

  canViewPocs(): boolean {
    return this.hasRole(RoleType.PocRead);
  }

  canEditInRoute(routeName: string): boolean {
    if (this.isAdministrator) {
      return true;
    }

    // Route-spezifische Berechtigungsprüfung
    const routeRoles: Record<string, RoleType[]> = {
      // Bewegungsdaten
      einlieferungen: [RoleType.EinlieferungWrite],
      ablieferungen: [RoleType.AblieferungWrite],
      abholungen: [RoleType.AbholungWrite],
      bestaende: [RoleType.BestandWrite],
      'tankstelle-bestand': [RoleType.Cockpit],
      nachweisverfolgung: [RoleType.Nachweisverfolgung],
      nachweisbescheinigungen: [RoleType.Nachweis],
      nachweise: [RoleType.ZertifikatWrite],
      bilanzen: [RoleType.BilanzWrite],

      // Stammdaten
      tauschpartner: [RoleType.PartnerWrite],
      standorte: [RoleType.StandortWrite],
      produkte: [RoleType.ProduktWrite],

      // Reports
      'reports-liste': [RoleType.Report],
      auswertungen: [RoleType.Report],

      // Dashboard ist für alle zugänglich
      dashboard: []
    };

    const requiredRoles = routeRoles[routeName];

    // Wenn keine spezifischen Rollen definiert sind, erlaube Zugriff
    if (!requiredRoles || requiredRoles.length === 0) {
      return true;
    }

    // Prüfe ob Benutzer erforderliche Rollen hat
    return this.hasAllRoles(requiredRoles);
  }

  // Router Guard Methoden
  canAccessRoute(routeName: string): boolean {
    if (this.isAdministrator) {
      return true;
    }

    // Route-spezifische Berechtigungsprüfung
    const routeRoles: Record<string, RoleType[]> = {
      // Bewegungsdaten
      einlieferungen: [RoleType.EinlieferungRead, RoleType.EinlieferungWrite],
      ablieferungen: [RoleType.AblieferungRead, RoleType.AblieferungWrite],
      abholungen: [RoleType.AbholungRead, RoleType.AbholungWrite],
      bestaende: [RoleType.BestandRead, RoleType.BestandWrite],
      'tankstelle-bestand': [RoleType.Cockpit],
      nachweisverfolgung: [RoleType.Nachweisverfolgung],
      nachweisbescheinigungen: [RoleType.Nachweis],
      nachweise: [RoleType.ZertifikatRead, RoleType.ZertifikatWrite],
      bilanzen: [RoleType.BilanzRead, RoleType.BilanzWrite],
      pocs: [RoleType.PocRead],
      'bewegungsdaten-import': [RoleType.ImportInbound, RoleType.ImportOutbound],

      // Stammdaten
      tauschpartner: [RoleType.PartnerRead, RoleType.PartnerWrite],
      standorte: [RoleType.StandortRead, RoleType.StandortWrite],
      produkte: [RoleType.ProduktRead, RoleType.ProduktWrite],
      'stammdaten-import': [RoleType.ProduktWrite, RoleType.StandortWrite, RoleType.PartnerWrite],

      // Reports
      'reports-liste': [RoleType.Report],
      'reports-ytd': [RoleType.YtdReport],
      auswertungen: [RoleType.Report],

      // Dashboard ist für alle zugänglich
      dashboard: []
    };

    const requiredRoles = routeRoles[routeName];

    // Wenn keine spezifischen Rollen definiert sind, erlaube Zugriff
    if (!requiredRoles || requiredRoles.length === 0) {
      return true;
    }

    // Prüfe ob Benutzer mindestens eine der erforderlichen Rollen hat
    return this.hasAnyRole(requiredRoles);
  }
}

export const permissionService = new PermissionService();
